// Read-only Anzeige der gespeicherten IMMOBILIENVERTRAGS-Daten in der
// Vertrags-Detailansicht. Die Anlage schreibt ~59 Merkmale (characteristicKeys.CCK)
// + Skalar-Felder; die Detailansicht zeigte davon bisher nichts (nur „Allgemein").
// Dieser Helfer verteilt alle gespeicherten Merkmale + Prämien-/Zahlungs-Skalare
// beschriftet auf die Sektionen „Vereinbarung", „Umfang" und „Vertragsdaten &
// Zahlung" — nicht gemappte Merkmale erscheinen unter „Umfang" (roh), damit
// NICHTS verborgen bleibt.
import type { AllgemeinField } from "./AllgemeinDetails";
import { CCK } from "../../immobilieContractStep/characteristicKeys";
import {
  formatThousands,
  labelForGefahrId,
  type GefahrenFormState,
} from "../../immobilieContractStep/gefahrenConfig";
import type { RiskSummary } from "../../kfzContractStep/types";

export type ImmoSectionKey = "vereinbarung" | "umfang" | "beitraege";

type CharLite = { nameBezeichnung?: string; contractCharacteristicValue?: string };
export type ImmoContractLite = {
  premiumAnnualGross?: number | null;
  premiumAnnualNet?: number | null;
  premiumByFrequencyGross?: number | null;
  paymentFrequency?: number | null;
  currency?: string | null;
  vatRate?: number | null;
  characteristics?: CharLite[];
};

// paymentFrequency wird von der Immobilien-Anlage als Raten/Jahr gespeichert.
const ZAHLWEISE_LABEL: Record<number, string> = {
  1: "Jährlich",
  2: "Halbjährlich",
  4: "Vierteljährlich",
  12: "Monatlich",
};

// CCK-Key → { Label, Sektion }. Kuratiert für die wichtigsten Felder; alle übrigen
// Merkmale landen über den Catch-all unter „Umfang".
const META: Record<string, { label: string; section: ImmoSectionKey }> = {
  [CCK.versicherer]: { label: "Versicherer", section: "vereinbarung" },
  [CCK.vertragsart]: { label: "Vertragsart", section: "vereinbarung" },
  [CCK.tarifierung]: { label: "Tarifierung / Produkt", section: "vereinbarung" },
  [CCK.vereinbarungStatus]: { label: "Vereinbarung", section: "vereinbarung" },
  [CCK.antragsart]: { label: "Antragsart", section: "vereinbarung" },
  [CCK.vorvertragExists]: { label: "Vorvertrag vorhanden", section: "vereinbarung" },
  [CCK.vorvertragIsFremd]: { label: "Fremdvertrag", section: "vereinbarung" },
  [CCK.vorvertragGesellschaft]: { label: "Vorvertrag – Gesellschaft", section: "vereinbarung" },
  [CCK.vorvertragNr]: { label: "Vorvertrag – Nummer", section: "vereinbarung" },
  [CCK.vorvertragAlreadyCancelled]: { label: "Vorvertrag bereits gekündigt", section: "vereinbarung" },
  [CCK.vorvertragKuendigung]: { label: "Kündigung zum", section: "vereinbarung" },
  [CCK.vorvertragKuendigungsgrund]: { label: "Kündigungsgrund", section: "vereinbarung" },
  [CCK.vorvertragShouldCancel]: { label: "Soll gekündigt werden", section: "vereinbarung" },

  [CCK.vs1914]: { label: "Wert 1914", section: "umfang" },
  [CCK.vsSumme1914]: { label: "Versicherungssumme 1914 (€)", section: "umfang" },
  [CCK.vs1980]: { label: "Wert 1980", section: "umfang" },
  [CCK.vsSumme1980]: { label: "Versicherungssumme 1980 (€)", section: "umfang" },
  [CCK.vsBaupreisindex]: { label: "Baupreisindex", section: "umfang" },
  [CCK.vsBaupreisindexJahr]: { label: "Baupreisindex – Jahr", section: "umfang" },
  [CCK.vsWertzuschlag]: { label: "Wertzuschlag (%)", section: "umfang" },
  [CCK.vsWertzuschlagJahr]: { label: "Wertzuschlag – Jahr", section: "umfang" },
  [CCK.heScope]: { label: "Höchstentschädigung – Umfang (alt)", section: "umfang" },
  [CCK.heValue]: { label: "Höchstentschädigung – Wert (alt)", section: "umfang" },
  [CCK.jheScope]: { label: "Jahreshöchstentschädigung – Umfang (alt)", section: "umfang" },
  [CCK.jheValue]: { label: "Jahreshöchstentschädigung – Wert (alt)", section: "umfang" },
  [CCK.mvScope]: { label: "Mietverlust – Umfang (alt)", section: "umfang" },
  [CCK.mvHaftzeit]: { label: "Mietverlust – Haftzeit (Monate)", section: "umfang" },
  [CCK.mvVsArt]: { label: "Mietverlust – VS-Art", section: "umfang" },
  [CCK.mvVsBrutto]: { label: "Mietverlust – VS brutto (€)", section: "umfang" },
  [CCK.mvAusschluss]: { label: "Mietverlust – Ausschluss", section: "umfang" },
  [CCK.uvvVariante]: { label: "Unterversicherungsverzicht – Variante", section: "umfang" },
  [CCK.uvvVereinbartJa]: { label: "Unterversicherungsverzicht vereinbart", section: "umfang" },
  [CCK.uvvSonstiges]: { label: "UVV – Sonstiges", section: "umfang" },
  [CCK.pvVorhanden]: { label: "Photovoltaik vorhanden", section: "umfang" },
  [CCK.pvPolicennr]: { label: "Photovoltaik – Policennr.", section: "umfang" },
  [CCK.volldeckungAb]: { label: "Volldeckung ab", section: "umfang" },

  [CCK.inkassoart]: { label: "Inkassoart", section: "beitraege" },
  [CCK.rechnungsstellung]: { label: "Rechnungsstellung", section: "beitraege" },
  [CCK.praemieDerzeit]: { label: "Prämie derzeit (€)", section: "beitraege" },
  [CCK.praemieVolldeckung]: { label: "Prämie Volldeckung (€)", section: "beitraege" },
  [CCK.praemieDifferenz]: { label: "Prämie Differenz (€)", section: "beitraege" },
  [CCK.bedingungenAllgemeinText]: { label: "Bedingungen (allgemein)", section: "beitraege" },
  [CCK.bedingungenBesondereText]: { label: "Bedingungen (besondere)", section: "beitraege" },
};

const euro = (v: number | null | undefined): string | null =>
  v === null || v === undefined
    ? null
    : `${v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Rohwert lesbar machen: Booleans → Ja/Nein, leere → weglassen, JSON gekürzt.
const fmtVal = (raw: string | undefined): string | null => {
  const v = (raw ?? "").trim();
  if (!v) return null;
  if (v === "true") return "Ja";
  if (v === "false") return "Nein";
  return v.length > 120 ? `${v.slice(0, 117)}…` : v;
};

// Merkmale, die als aufbereitete Gefahren-Zeilen erscheinen — nicht zusätzlich roh anzeigen.
const GEFAHREN_RAW_KEYS = new Set<string>([
  CCK.gefahrenJson,
  CCK.vorvertragGefahrenJson,
  CCK.hePerGefahrJson,
  CCK.jhePerGefahrJson,
  CCK.sbPerGefahrJson,
  CCK.mvHaftzeitPerGefahr,
  CCK.mvGefahren,
  "immo.gefahren_selected",
]);

const parseJson = <T,>(raw: string | undefined, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

/**
 * Rekonstruiert den Gefahren-Stand: neue Verträge tragen ihn komplett als JSON,
 * ältere nur als Auswahl-Liste plus getrennte Betrags-Maps.
 */
const readGefahren = (get: (k: string) => string | undefined, jsonKey: string): GefahrenFormState => {
  const fromJson = parseJson<GefahrenFormState>(get(jsonKey), {});
  if (Object.keys(fromJson).length) return fromJson;
  if (jsonKey !== CCK.gefahrenJson) return {};

  const state: GefahrenFormState = {};
  for (const id of (get("immo.gefahren_selected") ?? "").split(",").map((x) => x.trim()).filter(Boolean)) {
    state[id] = { sollVersichertWerden: "ja" };
  }
  const merge = (mapKey: string, field: "hoechstentschaedigung" | "jahreshoechstentschaedigung" | "selbstbeteiligung") => {
    for (const [id, v] of Object.entries(parseJson<Record<string, unknown>>(get(mapKey), {}))) {
      const digits = String(
        typeof v === "object" && v !== null ? (v as { value?: unknown }).value ?? "" : v,
      ).replace(/[^0-9]/g, "");
      if (digits) state[id] = { ...(state[id] ?? {}), [field]: digits };
    }
  };
  merge(CCK.hePerGefahrJson, "hoechstentschaedigung");
  merge(CCK.jhePerGefahrJson, "jahreshoechstentschaedigung");
  merge(CCK.sbPerGefahrJson, "selbstbeteiligung");
  for (const id of (get(CCK.mvGefahren) ?? "").split(",").map((x) => x.trim()).filter(Boolean)) {
    state[id] = { ...(state[id] ?? {}), mietverlust: "ja" };
  }
  return state;
};

/** Eine Zeile je versicherter Gefahr: „HE … · JHE … · SB … · Mietverlust: ja". */
const gefahrenFields = (state: GefahrenFormState, prefix: string): AllgemeinField[] => {
  const fields: AllgemeinField[] = [];
  for (const [id, input] of Object.entries(state)) {
    if (input?.sollVersichertWerden !== "ja") continue;
    const parts: string[] = [];
    if (input.hoechstentschaedigung) parts.push(`HE ${formatThousands(input.hoechstentschaedigung)} €`);
    if (input.jahreshoechstentschaedigung) parts.push(`JHE ${formatThousands(input.jahreshoechstentschaedigung)} €`);
    if (input.selbstbeteiligung) parts.push(`SB ${formatThousands(input.selbstbeteiligung)} €`);
    parts.push(`Mietverlust: ${input.mietverlust === "ja" ? "ja" : "nein"}`);
    fields.push({ label: `${prefix}${labelForGefahrId(id)}`, value: parts.join(" · ") });
  }
  return fields;
};

const prettifyKey = (key: string): string =>
  key.replace(/^immo\./, "").replace(/_/g, " ").replace(/\./g, " · ");

/** Baut die Read-only-Felder EINER Immobilien-Sektion aus Merkmalen + Skalaren. */
export function immoSectionFields(
  section: ImmoSectionKey,
  contract: ImmoContractLite | null,
): AllgemeinField[] {
  if (!contract) return [];
  const fields: AllgemeinField[] = [];

  // „Vertragsdaten & Zahlung": zuerst die Skalar-Felder (Prämie/Zahlweise/…).
  if (section === "beitraege") {
    const zw = contract.paymentFrequency;
    fields.push(
      { label: "Zahlweise", value: zw != null ? ZAHLWEISE_LABEL[zw] ?? String(zw) : null },
      { label: "Beitrag je Zahlweise (brutto)", value: euro(contract.premiumByFrequencyGross) },
      { label: "Jahresprämie (brutto)", value: euro(contract.premiumAnnualGross) },
      { label: "Jahresprämie (netto)", value: euro(contract.premiumAnnualNet) },
      { label: "Währung", value: contract.currency ?? null },
      {
        label: "USt-Satz",
        value: contract.vatRate != null ? `${(contract.vatRate * 100).toLocaleString("de-DE")} %` : null,
      },
    );
  }

  if (section === "umfang") {
    const get = (k: string): string | undefined =>
      (contract.characteristics ?? []).find((c) => c.nameBezeichnung === k)?.contractCharacteristicValue;
    fields.push(...gefahrenFields(readGefahren(get, CCK.gefahrenJson), "Gefahr: "));
    fields.push(...gefahrenFields(readGefahren(get, CCK.vorvertragGefahrenJson), "Vorvertrag – Gefahr: "));
  }

  for (const c of contract.characteristics ?? []) {
    const key = c.nameBezeichnung;
    if (!key) continue;
    if (GEFAHREN_RAW_KEYS.has(key)) continue; // oben bereits aufbereitet
    const meta = META[key];
    const targetSection: ImmoSectionKey = meta?.section ?? "umfang"; // Catch-all → Umfang
    if (targetSection !== section) continue;
    const value = fmtVal(c.contractCharacteristicValue);
    if (value === null) continue; // leere Merkmale nicht anzeigen
    fields.push({ label: meta?.label ?? prettifyKey(key), value });
  }

  return fields;
}

/** Read-only Liste der verknüpften Risikoobjekte für die „Risiken"-Sektion. */
export function immoRiskFields(risks: RiskSummary[]): AllgemeinField[] {
  if (!risks.length) return [];
  const fields: AllgemeinField[] = [];
  for (const r of risks) {
    fields.push({ label: "Objekt", value: r.name || `Risiko ${r.id}` });
    for (const [k, v] of Object.entries(r.characteristics ?? {})) {
      const value = fmtVal(v);
      if (value !== null) fields.push({ label: prettifyKey(k), value });
    }
  }
  return fields;
}
